import { Loader2, Plus, Tags, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Section } from '@/components/common/Blocks'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState } from '@/components/common/States'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { confirmModal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { type CustomerCategory, useCategories, useCategoryAdmin } from '@/features/crm/categoriesApi'
import { toastError } from '@/features/finance/ui'

/** Firma sahibinin müşteri kategorileri (Sıcak, Ilık, İlgisiz, İstemiyor vb.). Kullanılan kategori silinmez, pasife alınır. */
export function CustomerCategoriesPage() {
  const { t } = useTranslation()
  const categories = useCategories()
  const { save, remove, tones } = useCategoryAdmin()
  const [name, setName] = useState('')
  const [color, setColor] = useState<BadgeTone>('neutral')

  const toneOptions = tones.map((x) => ({ value: x, label: t(`admin.categories.tones.${x}`) }))
  const onAdd = () =>
    save.mutate(
      { input: { name: name.trim(), color, sort_order: ((categories.data?.length ?? 0) + 1) * 10, is_active: true } },
      {
        onSuccess: () => {
          setName('')
          toast.success(t('admin.categories.saved'))
        },
        onError: toastError,
      },
    )
  const update = (c: CustomerCategory, patch: Partial<{ name: string; color: string; is_active: boolean }>) =>
    save.mutate(
      { id: c.id, input: { name: c.name, color: c.color, sort_order: c.sortOrder, is_active: c.isActive, ...patch } },
      { onSuccess: () => toast.success(t('admin.categories.saved')), onError: toastError },
    )
  const onDelete = async (c: CustomerCategory) => {
    if (!(await confirmModal({ title: t('admin.categories.deleteTitle'), description: t(c.inUse ? 'admin.categories.deleteInUse' : 'admin.categories.deleteBody', { name: c.name, count: c.inUse }), confirmLabel: t('common.delete'), destructive: true }))) return
    remove.mutate(c.id, { onSuccess: (r) => toast.success(t((r as { deleted?: boolean })?.deleted ? 'common.deleted' : 'admin.categories.deactivated')), onError: toastError })
  }

  return (
    <>
      <PageHeader eyebrow={t('nav.groups.admin')} title={t('admin.categories.title')} description={t('admin.categories.subtitle')} />
      <div className="space-y-4">
        <Section title={t('admin.categories.add')} icon={Plus}>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-48 flex-1 space-y-1.5">
              <Label htmlFor="cc-name">{t('admin.categories.name')}</Label>
              <Input id="cc-name" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="w-40 space-y-1.5">
              <Label htmlFor="cc-color">{t('admin.categories.color')}</Label>
              <Select id="cc-color" value={color} onChange={setColor} options={toneOptions} />
            </div>
            <Button onClick={onAdd} disabled={!name.trim() || save.isPending}>
              {save.isPending ? <Loader2 className="animate-spin" /> : <Plus />}
              {t('common.save')}
            </Button>
          </div>
        </Section>
        <Section title={t('admin.categories.listTitle')} icon={Tags} bodyClassName="p-0">
          {categories.isLoading ? (
            <Skeleton className="m-4 h-32" />
          ) : !categories.data?.length ? (
            <EmptyState icon={Tags} title={t('admin.categories.empty')} />
          ) : (
            <ul className="divide-y">
              {categories.data.map((c) => (
                <li key={c.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <Badge tone={c.color}>{c.name}</Badge>
                  <span className="flex-1 text-xs text-muted-foreground">{t('admin.categories.inUse', { count: c.inUse })}</span>
                  <Select size="sm" value={c.color} onChange={(v) => update(c, { color: v })} options={toneOptions} aria-label={t('admin.categories.color')} />
                  <label className="flex items-center gap-1.5 text-sm">
                    <input type="checkbox" className="size-4 accent-primary" checked={c.isActive} onChange={(e) => update(c, { is_active: e.target.checked })} />
                    {t('admin.categories.active')}
                  </label>
                  <Button size="icon" variant="ghost" aria-label={t('common.delete')} onClick={() => void onDelete(c)}>
                    <Trash2 />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </>
  )
}
